import React, { ComponentType } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

/* eslint frontbucket-patterns/no-old-dropdown-menu: "warn" */
import {
  DropdownItem as AkDropdownItem,
  DropdownItemGroup as AkDropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import {
  CustomItem,
  MenuGroup,
  LinkItem,
  MenuGroupProps,
  LinkItemProps,
  Section,
} from '@atlaskit/menu';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

// This file provides abstractions for the 2 Atlaskit packages used for dropdown menus, since the
// 2 different navigation components use different ones and they have different APIs:
// * @atlaskit/navigation-next      => uses @atlaskit/dropdown-item
// * @atlaskit/atlassian-navigation => uses @atlaskit/menu
// Once the "fd-horizontal-nav" feature flag is removed, this abstraction can be removed.

const DropdownDivider = styled.div`
  border-top: 2px solid ${token('color.border', colors.N30A)};
  margin-top: 6px;
  margin-bottom: 6px;
`;

type DropdownItemProps = LinkItemProps & {
  isAkDropdown?: boolean;
  linkComponent?: ComponentType<any>;
};

export const DropdownItem = ({
  isAkDropdown,
  iconBefore,
  iconAfter,
  linkComponent,
  ...props
}: DropdownItemProps): JSX.Element => {
  return isAkDropdown ? (
    <AkDropdownItem
      elemBefore={iconBefore}
      elemAfter={iconAfter}
      linkComponent={linkComponent}
      {...props}
    />
  ) : linkComponent ? (
    <CustomItem
      component={linkComponent}
      iconBefore={iconBefore}
      iconAfter={iconAfter}
      {...props}
    />
  ) : (
    <LinkItem iconBefore={iconBefore} iconAfter={iconAfter} {...props} />
  );
};

type DropdownItemGroupProps = MenuGroupProps & {
  title?: string;
  isAkDropdown?: boolean;
  hasSeparator?: boolean;
};

export const DropdownItemGroup = ({
  isAkDropdown,
  hasSeparator = false,
  ...props
}: DropdownItemGroupProps): JSX.Element => {
  if (isAkDropdown) {
    return (
      <>
        {hasSeparator && <DropdownDivider />}
        <AkDropdownItemGroup {...props} />
      </>
    );
  }

  const { children, title, ...menuProps } = props;
  return (
    <MenuGroup {...menuProps}>
      <Section title={title} hasSeparator={hasSeparator}>
        {children}
      </Section>
    </MenuGroup>
  );
};
